import React, { useState } from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { Row, Box } from '../../legacy-design-system/form'
import {
  DialogBody,
  Dialog,
  DialogFooter
} from '../../legacy-design-system/modal'
import { TypedServers, TypedVulnerabilities } from '../shared'
import { ServerEnvironment } from '../server-environment'
import { ViewerLoader } from '../viewer'
import { RouteContext } from '../route-provider'
import { getApplicationDrilldownID } from '../route'
import { AttestationReportLoader } from './attestation-report-loader'
import { AttestationReportRequest } from './index'
import { GenerateAttestationReportMutation } from './attestation-report-mutation'
import { logError, logSuccess } from '../toaster'
import { ReportingStatusQuery } from './reporting-status-query'
import { DefaultCompliancePolicy } from '../policy'
import { ReportingInfoQuery } from './reporting-info-query'
import {
  AttestationForm,
  AttestationFormProps,
  LimitsReportError
} from './attestation-form'
import { Button } from '@contrast-security-inc/design-system-buttons'

export const testID = {
  dialog: 'pdf-report-dialog',
  generateBtn: 'generate-btn'
}

interface Props {
  onCloseDialog: () => void
}

interface FormValue {
  readonly vulnRules: TypedVulnerabilities[]
  readonly vulnStatuses: TypedVulnerabilities[]
  readonly vulnSeverities: TypedVulnerabilities[]
  readonly vulnTags: TypedVulnerabilities[]
  readonly isVulnDetailsEnabled: boolean
  readonly serverTags: TypedServers[]
  readonly serverEnvironments: TypedServers[]
  readonly complianceReports: DefaultCompliancePolicy[]
  readonly isRouteObservationsEnabled: boolean
}

interface State {
  readonly formValue: FormValue
}

export function AttestationReportDialog({ onCloseDialog }: Props) {
  const formSetter = <K extends keyof FormValue>(
    key: K
  ): ((v: FormValue[K]) => void) => {
    return (value) =>
      setState(({ formValue }) => ({
        formValue: {
          ...formValue,
          [key]: value
        }
      }))
  }

  const [state, setState] = useState<State>({
    formValue: {
      vulnRules: [],
      vulnSeverities: [],
      vulnStatuses: [],
      vulnTags: [],
      serverEnvironments: [],
      serverTags: [],
      isVulnDetailsEnabled: false,
      complianceReports: [],
      isRouteObservationsEnabled: false
    }
  })
  const { formatMessage } = useIntl()

  const {
    formValue: {
      vulnRules,
      vulnSeverities,
      vulnStatuses,
      vulnTags,
      serverTags,
      serverEnvironments,
      complianceReports,
      isVulnDetailsEnabled,
      isRouteObservationsEnabled
    }
  } = state

  return (
    <RouteContext.Consumer>
      {({ parameters }) => {
        const appID = getApplicationDrilldownID(parameters)

        if (!appID) {
          throw new Error('Unable to parse application ID from route')
        }

        return (
          <ViewerLoader>
            {({ viewer, loading }) => {
              if (!viewer || loading) {
                return null
              }

              const orgUuid = viewer.currentOrganization.id

              return (
                <ReportingStatusQuery>
                  {({ status: reportingStatus }) => {
                    const isReportConfigMissing =
                      !reportingStatus || !reportingStatus.enabled

                    return (
                      <ReportingInfoQuery
                        applicationID={appID}
                        organizationID={orgUuid}
                        filters={mapAttestationForm(state)}
                      >
                        {({
                          refetch,
                          vulnTotal,
                          vulnerabilitiesWithDetailsLimit,
                          vulnerabilitiesWithoutDetailsLimit,
                          routesWithDetailsLimit,
                          routesWithoutDetailsLimit,
                          maxVulnsAndRoutesWithDetails,
                          maxVulnsAndRoutesWithoutDetails,
                          totalRoutes
                        }) => {
                          return (
                            <GenerateAttestationReportMutation>
                              {({
                                generateAttestationReport: generateReport
                              }) => {
                                const isFormEnabled = !isReportConfigMissing

                                const limitErrors =
                                  areValidVulnerabilitiesAndRoutesLimits(
                                    vulnTotal,
                                    totalRoutes,
                                    vulnerabilitiesLimit(
                                      isVulnDetailsEnabled,
                                      vulnerabilitiesWithDetailsLimit,
                                      vulnerabilitiesWithoutDetailsLimit
                                    ),
                                    routesLimit(
                                      isRouteObservationsEnabled,
                                      routesWithDetailsLimit,
                                      routesWithoutDetailsLimit
                                    ),
                                    vulnerabilitiesAndRoutesLimit(
                                      isRouteObservationsEnabled,
                                      maxVulnsAndRoutesWithDetails,
                                      maxVulnsAndRoutesWithoutDetails
                                    )
                                  )

                                const attestationFormProps: AttestationFormProps =
                                  {
                                    isReportConfigMissing,
                                    limitErrors,
                                    isVulnDetailsEnabled,
                                    vulnTotal,
                                    totalRoutes,
                                    vulnsSelection: [
                                      ...vulnStatuses,
                                      ...vulnSeverities,
                                      ...vulnRules,
                                      ...vulnTags
                                    ],
                                    serversSelection: [
                                      ...serverEnvironments,
                                      ...serverTags
                                    ],
                                    complianceReportsSelection:
                                      complianceReports,
                                    isRouteObservationsEnabled,
                                    onChangeVulns: async (
                                      severities,
                                      rules,
                                      statuses,
                                      tags
                                    ) => {
                                      onChangeVulns(
                                        formSetter,
                                        severities,
                                        rules,
                                        statuses,
                                        tags
                                      )
                                      await refetch()
                                    },
                                    onChangeServers: async (
                                      environments,
                                      tags
                                    ) => {
                                      formSetter('serverEnvironments')(
                                        environments
                                      )
                                      formSetter('serverTags')(tags)
                                      await refetch()
                                    },

                                    onChangeComplianceReports:
                                      formSetter('complianceReports'),
                                    onCheckVulnDetailsEnabled: formSetter(
                                      'isVulnDetailsEnabled'
                                    ),
                                    onCheckRouteObservationsEnabled: formSetter(
                                      'isRouteObservationsEnabled'
                                    ),
                                    vulnRules: [],
                                    vulnTags: [],
                                    serverTags: [],
                                    compliancePolicies: []
                                  }

                                return (
                                  <Dialog
                                    testId={testID.dialog}
                                    isOpen={true}
                                    size="large"
                                    onCancel={onCloseDialog}
                                    renderTitle={() =>
                                      formatMessage({
                                        id: 'title_report_attestation'
                                      })
                                    }
                                  >
                                    <DialogBody>
                                      <form
                                        noValidate
                                        className={css`
                                          padding: 0 5px;
                                        `}
                                      >
                                        <Row>
                                          <Box basis={12}>
                                            <div
                                              className={css`
                                                margin-top: 4px;
                                                margin-bottom: 10px;
                                                margin-right: 10px;
                                              `}
                                            >
                                              {formatMessage({
                                                id: 'detail_applicationAttestationReport_header'
                                              })}
                                            </div>
                                          </Box>
                                        </Row>
                                        {isFormEnabled ? (
                                          <AttestationReportLoader
                                            organizationID={orgUuid}
                                            applicationID={appID}
                                          >
                                            {({
                                              assessRules,
                                              applicationVulnerabilityTags,
                                              applicationServerTags,
                                              compliancePolicies
                                            }) => {
                                              return (
                                                <AttestationForm
                                                  {...attestationFormProps}
                                                  vulnRules={assessRules}
                                                  vulnTags={
                                                    applicationVulnerabilityTags
                                                  }
                                                  serverTags={
                                                    applicationServerTags
                                                  }
                                                  compliancePolicies={
                                                    compliancePolicies
                                                  }
                                                />
                                              )
                                            }}
                                          </AttestationReportLoader>
                                        ) : (
                                          <AttestationForm
                                            {...attestationFormProps}
                                          />
                                        )}
                                      </form>
                                    </DialogBody>

                                    <DialogFooter>
                                      <Button
                                        variant="secondary"
                                        className={css`
                                          margin-left: 8px;
                                          height: 34px;
                                          font-size: 14px;
                                        `}
                                        onClick={onCloseDialog}
                                      >
                                        {formatMessage({
                                          id: 'action_cancel'
                                        })}
                                      </Button>

                                      <Button
                                        variant="primary"
                                        data-testid={testID.generateBtn}
                                        isDisabled={
                                          !isFormEnabled ||
                                          limitErrors.length > 0
                                        }
                                        className={css`
                                          margin-left: 8px;
                                          height: 34px;
                                          font-size: 14px;
                                        `}
                                        onClick={() => {
                                          generateReport({
                                            organizationID: orgUuid,
                                            applicationID: appID,
                                            request: mapAttestationForm(state)
                                          })
                                            .then(() =>
                                              logSuccess(
                                                formatMessage({
                                                  id: 'detail_attestationReport_successfullyReportInitialization'
                                                })
                                              )
                                            )
                                            .catch(() => {
                                              return logError(
                                                formatMessage({
                                                  id: 'detail_attestationReport_failedReportInitialization'
                                                })
                                              )
                                            })

                                          onCloseDialog()
                                        }}
                                      >
                                        {formatMessage({
                                          id: 'action_applicationReports_generate'
                                        })}
                                      </Button>
                                    </DialogFooter>
                                  </Dialog>
                                )
                              }}
                            </GenerateAttestationReportMutation>
                          )
                        }}
                      </ReportingInfoQuery>
                    )
                  }}
                </ReportingStatusQuery>
              )
            }}
          </ViewerLoader>
        )
      }}
    </RouteContext.Consumer>
  )
}

export function onChangeVulns(
  formSetterFunction,
  severities: TypedVulnerabilities[],
  rules: TypedVulnerabilities[],
  statuses: TypedVulnerabilities[],
  tags: TypedVulnerabilities[]
) {
  formSetterFunction('vulnRules')(rules)
  formSetterFunction('vulnSeverities')(severities)
  formSetterFunction('vulnStatuses')(statuses)
  formSetterFunction('vulnTags')(tags)
}

export const mapAttestationForm = (state: State): AttestationReportRequest => {
  const {
    formValue: {
      vulnStatuses,
      vulnSeverities,
      vulnTags,
      vulnRules,
      serverTags,
      serverEnvironments,
      complianceReports,
      isVulnDetailsEnabled,
      isRouteObservationsEnabled
    }
  } = state

  return {
    vulnerabilityTypes: vulnRules.map((rule) => rule.id),
    vulnerabilityStatuses: vulnStatuses.map((status) => status.id),
    vulnerabilitySeverities: vulnSeverities.map((severity) => severity.id),
    vulnerabilityTags: vulnTags.map((tag) => tag.id),
    serverEnvironments: serverEnvironments.map(
      (env) => env.id as ServerEnvironment
    ),
    serverTags: serverTags.map((tag) => tag.id),
    complianceReports: complianceReports.map(
      (complianceReport) => complianceReport.key
    ),
    showVulnerabilitiesDetails: isVulnDetailsEnabled,
    showRouteObservations: isRouteObservationsEnabled
  }
}

export const areValidVulnerabilitiesAndRoutesLimits = (
  totalVulnerabilities: number,
  totalRoutes: number,
  vulnerabilitiesLimit: number,
  routesLimit: number,
  vulnerabilitiesAndRoutesLimit: number
): LimitsReportError[] => {
  const vulnsAndRoutesTotal: number = totalVulnerabilities + totalRoutes
  const errors: LimitsReportError[] = []

  if (totalVulnerabilities > vulnerabilitiesLimit) {
    errors.push({
      errorType: 'VULS_LIMIT_ERROR',
      error:
        'detail_applicationAttestationReport_maxVulnerabilities_errorMessage',
      limit: vulnerabilitiesLimit,
      total: totalVulnerabilities
    })
  }

  if (totalRoutes > routesLimit) {
    errors.push({
      errorType: 'ROUTE_LIMIT_ERROR',
      error: 'detail_applicationAttestationReport_maxRoutes_errorMessage',
      limit: routesLimit,
      total: totalRoutes
    })
  }

  if (vulnsAndRoutesTotal > vulnerabilitiesAndRoutesLimit) {
    errors.push({
      errorType: 'VULNS_AND_ROUTES_LIMIT_ERROR',
      error:
        'detail_applicationAttestationReport_maxVulnsAndRoutes_errorMessage',
      limit: vulnerabilitiesAndRoutesLimit,
      total: vulnsAndRoutesTotal
    })
  }

  return errors
}

export const vulnerabilitiesLimit = (
  showVulnerabilitiesDetails: boolean,
  vulnerabilitiesWithDetailsLimit: number,
  vulnerabilitiesWithoutDetailsLimit: number
): number =>
  showVulnerabilitiesDetails
    ? vulnerabilitiesWithDetailsLimit
    : vulnerabilitiesWithoutDetailsLimit

export const routesLimit = (
  showRouteObservations: boolean,
  routesWithDetailsLimit: number,
  routesWithoutDetailsLimit: number
) =>
  showRouteObservations ? routesWithDetailsLimit : routesWithoutDetailsLimit

export const vulnerabilitiesAndRoutesLimit = (
  showRouteObservations: boolean,
  maxVulnsAndRoutesWithDetails: number,
  maxVulnsAndRoutesWithoutDetails: number
) =>
  showRouteObservations
    ? maxVulnsAndRoutesWithDetails
    : maxVulnsAndRoutesWithoutDetails
